<template>
  <div id="invoices-modal-app">
    <modal
      elem-id="modal_invoice_detail"
      size="medium"
      :close-label="t('Fechar')"
      :title="modalTitle"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal">
      <template v-if="isVisible">
        <div v-if="loading" class="loading-state" data-testid="invoice-detail-loading">
          <i class="pi pi-spinner pi-spin"></i> {{ t('Carregando...') }}
        </div>
        <div v-else-if="invoice" class="invoice-detail">
          <div class="grid">
            <div class="field"><label>{{ t('Número') }}</label><span data-testid="invoice-detail-number">{{ invoice.invoice_number || '—' }}</span></div>
            <div class="field"><label>{{ t('Status') }}</label>
              <Tag
                data-testid="invoice-detail-status"
                :severity="statusSeverity(invoice.status)"
                :value="statusLabel(invoice.status)" />
            </div>
            <div class="field"><label>{{ t('Emissão') }}</label><span>{{ invoice.emission_date || '—' }}</span></div>
            <div class="field"><label>{{ t('Autorização') }}</label><span>{{ invoice.authority_date || '—' }}</span></div>
            <div class="field"><label>{{ t('Provedor') }}</label><span>{{ invoice.provider_code || '—' }}</span></div>
            <div class="field"><label>{{ t('External ID') }}</label><span>{{ invoice.external_id || '—' }}</span></div>
            <div class="field"><label>{{ t('Tipo de documento') }}</label><span data-testid="invoice-detail-document-kind">{{ invoice.document_kind || '—' }}</span></div>
          </div>

          <h4>{{ t('Cliente') }}</h4>
          <div class="grid">
            <div class="field"><label>{{ t('Nome') }}</label><span>{{ invoice.customer_name || '—' }}</span></div>
            <div class="field"><label>{{ t('RUT / CPF') }}</label><span>{{ invoice.customer_doc || '—' }}</span></div>
            <div class="field"><label>{{ t('E-mail') }}</label><span>{{ invoice.customer_email || '—' }}</span></div>
          </div>

          <h4>{{ t('Totais') }}</h4>
          <div class="grid">
            <div class="field"><label>{{ t('Subtotal') }}</label><span>{{ formatCurrency(invoice.taxless_amount) }}</span></div>
            <div class="field"><label>{{ t('Impostos') }}</label><span>{{ formatCurrency(invoice.tax_amount) }}</span></div>
            <div class="field"><label>{{ t('Total') }}</label><span>{{ formatCurrency(invoice.total_amount) }}</span></div>
          </div>

          <div class="actions" v-if="!cancelMode">
            <Button
              v-if="invoice.pdf_url"
              :label="t('Baixar PDF')"
              icon="pi pi-file-pdf"
              size="small"
              data-testid="invoice-btn-pdf"
              @click="handleDownloadPdf" />
            <Button
              v-if="invoice.xml_url"
              :label="t('Baixar XML')"
              icon="pi pi-file"
              size="small"
              data-testid="invoice-btn-xml"
              @click="handleDownloadXml" />
            <Button
              v-if="invoice.customer_email"
              :label="t('Reenviar e-mail')"
              icon="pi pi-envelope"
              severity="secondary"
              size="small"
              data-testid="invoice-btn-email"
              @click="handleSendEmail" />
            <Button
              v-if="canCancel"
              :label="t('Cancelar fatura')"
              icon="pi pi-times"
              severity="danger"
              size="small"
              data-testid="invoice-btn-cancel"
              @click="startCancel" />
          </div>

          <box
            v-if="cancelMode"
            type="warning"
            :title="t('Cancelamento definitivo')"
            data-testid="invoice-cancel-confirm">
            <p>
              {{ t('Você está prestes a cancelar definitivamente a nota {number}. Esta ação não pode ser desfeita.', { number: invoice.invoice_number || ('#' + invoice.id) }) }}
            </p>
            <p data-testid="invoice-cancel-prazo-aviso">
              <template v-if="invoice.document_kind === 'nfe'">
                {{ t('NF-e só pode ser cancelada em até 24 horas após a emissão (alguns estados permitem prazo maior). Passado o prazo, o caminho é a nota de devolução.') }}
              </template>
              <template v-else>
                {{ t('O prazo de cancelamento varia por prefeitura, e algumas não permitem cancelamento. Se recusado, a mensagem da prefeitura será exibida.') }}
              </template>
            </p>

            <input-text-area
              elem-id="invoice_cancel_justificativa"
              v-model="justificativa"
              :rows="4"
              :required="justificativaRequired"
              :label="t('Justificativa')"
              :placeholder="t('Descreva o motivo do cancelamento (15 a 255 caracteres)')"
              data-testid="invoice-cancel-justificativa-input">
            </input-text-area>
            <small v-if="justificativaRequired && !isJustificativaValid" class="p-error">
              {{ t('Obrigatório: 15 a 255 caracteres.') }}
            </small>

            <div class="actions" style="margin-top: 1rem;">
              <Button
                :label="t('Voltar')"
                severity="secondary"
                size="small"
                :disabled="isCancelling"
                data-testid="invoice-cancel-back"
                @click="backFromCancel" />
              <Button
                :label="t('Confirmar cancelamento definitivo')"
                icon="pi pi-times"
                severity="danger"
                size="small"
                :disabled="!isJustificativaValid || isCancelling"
                data-testid="invoice-cancel-confirm-btn"
                @click="handleConfirmCancel" />
            </div>
          </box>
        </div>
        <div v-else class="loading-state" data-testid="invoice-detail-empty">
          {{ t('Fatura não encontrada.') }}
        </div>
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';
import { usePaymentInvoices } from '../../../composables/usePaymentInvoices';

/** Mirrors the backend InvoiceService::cancelar() rule: mandatory (15-255
 *  chars) only when the franchise's provider is 'focus_nfe' (Brazil). */
const JUSTIFICATIVA_MIN_LENGTH = 15;
const JUSTIFICATIVA_MAX_LENGTH = 255;

type InvoiceDetail = {
  id: number;
  invoice_number: string | null;
  external_id: string | null;
  status: string;
  document_kind: string | null;
  customer_name: string | null;
  customer_doc: string | null;
  customer_email: string | null;
  total_amount: number | null;
  tax_amount: number | null;
  taxless_amount: number | null;
  emission_date: string | null;
  authority_date: string | null;
  pdf_url: string | null;
  xml_url: string | null;
  provider_code: string | null;
};

const { t, n } = useI18n();
const { getInvoice, cancelInvoice, sendEmail, downloadPdf, downloadXml } = usePaymentInvoices();

const emit = defineEmits<{
  close: [];
}>();

const isVisible = ref<boolean>(false);
const loading = ref<boolean>(false);
const invoice = ref<InvoiceDetail | null>(null);

// Cancel confirmation sub-view state (irreversible action — requires an
// explicit, in-modal confirmation step naming the note, not a bare confirm popup).
const cancelMode = ref<boolean>(false);
const justificativa = ref<string>('');
const isCancelling = ref<boolean>(false);

const modalTitle = computed<string>(() => t('Detalhe da Fatura'));

// Only an AUTHORIZED note can be cancelled — offering the action for a note
// still processing (submitted) or already terminal is offering a guaranteed error.
const canCancel = computed<boolean>(() => {
  if (!invoice.value) return false;
  return invoice.value.status === 'authorized';
});

const justificativaRequired = computed<boolean>(() => invoice.value?.provider_code === 'focus_nfe');

const isJustificativaValid = computed<boolean>(() => {
  if (!justificativaRequired.value) return true;
  const len = justificativa.value.trim().length;
  return len >= JUSTIFICATIVA_MIN_LENGTH && len <= JUSTIFICATIVA_MAX_LENGTH;
});

const statusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    draft:      t('Rascunho'),
    submitted:  t('Enviada'),
    authorized: t('Autorizada'),
    rejected:   t('Rejeitada'),
    cancelled:  t('Cancelada'),
    error:      t('Erro'),
  };
  return labels[status] || status;
};

const statusSeverity = (status: string): string => {
  const map: Record<string, string> = {
    draft:      'secondary',
    submitted:  'info',
    authorized: 'success',
    rejected:   'danger',
    cancelled:  'secondary',
    error:      'danger',
  };
  return map[status] || 'secondary';
};

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '—';
  try {
    return n(value, 'currency');
  } catch {
    return String(value);
  }
};

const resetCancelState = (): void => {
  cancelMode.value = false;
  justificativa.value = '';
  isCancelling.value = false;
};

const openForId = async (id: number): Promise<void> => {
  log('InvoicesModal', 'openForId', 'Opening detail', id);
  isVisible.value = true;
  loading.value = true;
  invoice.value = null;
  resetCancelState();
  try {
    const result = await getInvoice(id) as InvoiceDetail | null;
    invoice.value = result;
  } finally {
    loading.value = false;
  }
};

const closeModal = (): void => {
  log('InvoicesModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  invoice.value = null;
  resetCancelState();
  emit('close');
};

const handleDownloadPdf = (): void => {
  if (!invoice.value) return;
  log('InvoicesModal', 'handleDownloadPdf', 'Downloading PDF', invoice.value.id);
  // The modal already holds the detail — pass pdf_url to skip a re-fetch.
  downloadPdf(invoice.value.id, invoice.value.pdf_url);
};

const handleDownloadXml = (): void => {
  if (!invoice.value) return;
  log('InvoicesModal', 'handleDownloadXml', 'Downloading XML', invoice.value.id);
  // The modal already holds the detail — pass xml_url to skip a re-fetch.
  downloadXml(invoice.value.id, invoice.value.xml_url);
};

const handleSendEmail = async (): Promise<void> => {
  if (!invoice.value) return;
  try {
    await sendEmail(invoice.value.id);
    eventBus.emit('globalMessage.success', t('E-mail reenviado com sucesso'));
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    eventBus.emit('globalMessage.error', message || t('Erro ao enviar e-mail'));
  }
};

// Cancellation is irreversible — this opens an in-modal confirmation
// sub-view (naming the note, collecting the justificativa) instead of
// firing the action directly.
const startCancel = (): void => {
  if (!invoice.value) return;
  justificativa.value = '';
  cancelMode.value = true;
};

const backFromCancel = (): void => {
  cancelMode.value = false;
  justificativa.value = '';
};

const handleConfirmCancel = async (): Promise<void> => {
  if (!invoice.value || !isJustificativaValid.value || isCancelling.value) return;
  isCancelling.value = true;
  try {
    await cancelInvoice(invoice.value.id, justificativa.value.trim());
    eventBus.emit('globalMessage.success', t('Fatura cancelada'));
    eventBus.emit('invoices.refresh');
    closeModal();
  } catch (err) {
    // The provider/backend refused the cancellation (e.g. a 200-with-error-body
    // from Focus NFe, or a status/permission gate) — the local record was NOT
    // touched, so we stay in cancelMode and surface the reason to the user.
    const message = err instanceof Error ? err.message : String(err);
    eventBus.emit('globalMessage.error', message || t('Erro ao cancelar fatura'));
    isCancelling.value = false;
  }
};

interface ModalOpenPayload {
  id?: number;
}

const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  if (data?.id) {
    await openForId(Number(data.id));
  }
};

onMounted(() => {
  log('InvoicesModal', 'mounted', 'Mounted');
  eventBus.on('modal.invoices.open', handleModalOpen);
  eventBus.on('modal.invoices.close', closeModal);
});

onBeforeUnmount(() => {
  eventBus.off('modal.invoices.open', handleModalOpen);
  eventBus.off('modal.invoices.close', closeModal);
});

defineExpose({ openForId, closeModal });
</script>

<style scoped>
.invoice-detail h4 {
  margin: 1rem 0 0.5rem;
  border-bottom: 1px solid var(--surface-200, #e5e7eb);
  padding-bottom: 0.25rem;
}
.invoice-detail .grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 0.5rem 1rem;
}
.invoice-detail .field {
  display: flex;
  flex-direction: column;
}
.invoice-detail .field label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: var(--text-color-secondary, #6b7280);
}
.invoice-detail .field span {
  font-weight: 500;
}
.invoice-detail .actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}
.loading-state {
  padding: 1rem;
  text-align: center;
  color: var(--text-color-secondary, #6b7280);
}
</style>
